import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useMemo } from 'react';
import { cn } from '@/app/utils';
import { UPSCALE_RESOLUTION_PRIORITY, type UpscaleSourceResolution, type UpscaleTargetResolution } from '../../_utils';

interface UpscaleResolutionSelectorProps {
  selectedResolution: string;
  onResolutionChange: (resolution: UpscaleTargetResolution) => void;
  currentResolution?: UpscaleSourceResolution;
  duration?: number;
  className?: string;
}

export const UpscaleResolutionSelector: FC<UpscaleResolutionSelectorProps> = ({
  selectedResolution,
  onResolutionChange,
  currentResolution,
  className,
}) => {
  const t = useTranslations();

  const resolutionOptions = useMemo(() => {
    const baseOptions = [
      {
        value: '1080p' as const,
        label: '1080P',
        description: t('tools.upscale.socialMedia'),
      },
      {
        value: '2k' as const,
        label: '2K',
        description: t('tools.upscale.feedAds'),
      },
      {
        value: '4k' as const,
        label: '4K',
        description: t('tools.upscale.tvcAds'),
      },
    ];

    if (!currentResolution) {
      return baseOptions.map((option) => ({ ...option, disabled: false }));
    }

    const currentPriority = currentResolution === '720p' ? 0 : UPSCALE_RESOLUTION_PRIORITY[currentResolution];

    return baseOptions.map((option) => ({
      ...option,
      disabled: UPSCALE_RESOLUTION_PRIORITY[option.value] <= currentPriority,
    }));
  }, [currentResolution, t]);

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center overflow-hidden rounded-16 border border-system-white08 bg-system-white04 font-normal text-white',
        className,
      )}
    >
      {resolutionOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => !option.disabled && onResolutionChange(option.value)}
          disabled={option.disabled}
          className={cn(
            'flex flex-1 flex-col items-center justify-center rounded-16 border-2 border-transparent px-4 py-4 transition-all',
            selectedResolution === option.value && !option.disabled && 'border-[#5765f2] bg-[rgba(87,101,242,0.2)]',
            option.disabled && 'cursor-not-allowed opacity-40',
            !option.disabled && 'hover:bg-system-white08',
          )}
        >
          <p className="text-sm leading-[1.4]">{option.label}</p>
          <p className="text-white/40 text-xs leading-[1.4]">{option.description}</p>
        </button>
      ))}
    </div>
  );
};
